<template>
	<v-row class="full-height quotes-page">
		<v-col cols="12" class="flex-none pb-0">
			<v-card class="quotes-toolbar-card" flat>
				<v-card-text class="pa-3">
					<v-row align="center" dense>
						<!-- Search -->
						<v-col cols="12" sm="6" md="4" lg="3">
							<v-text-field
								v-model="searchForm.keywords"
								placeholder="Search quotes..."
								prepend-inner-icon="mdi-magnify"
								outlined
								dense
								hide-details
								clearable
								class="quotes-search"
								@input="onSearchInput"
								@click:clear="onSearchClear"
							></v-text-field>
						</v-col>

						<!-- Filters -->
						<v-col cols="6" sm="3" md="2">
							<v-select
								v-model="selectedFilters"
								:items="filterOptions"
								label="Status"
								multiple
								clearable
								outlined
								dense
								hide-details
								@change="onFilterChange"
								@click:clear="onFilterClear"
							>
								<template v-slot:selection="{ item, index }">
									<span v-if="index === 0" class="text-truncate">{{ item.text }}</span>
									<span v-if="index === 1" class="grey--text caption ml-1">
										+{{ selectedFilters.length - 1 }}
									</span>
								</template>
							</v-select>
						</v-col>

						<v-col cols="6" sm="3" md="2" v-if="canFilterByRep">
							<v-select
								v-model="selectedSalesRepIds"
								:items="salesRepOptions"
								item-text="name"
								item-value="salesRepId"
								label="Sales Rep"
								multiple
								clearable
								outlined
								dense
								hide-details
								@change="onFilterChange"
								@click:clear="onFilterClear"
							>
								<template v-slot:selection="{ item, index }">
									<span v-if="index === 0" class="text-truncate">{{ item.name }}</span>
									<span v-if="index === 1" class="grey--text caption ml-1">
										+{{ selectedSalesRepIds.length - 1 }}
									</span>
								</template>
							</v-select>
						</v-col>

						<!-- Spacer -->
						<v-spacer class="d-none d-md-flex"></v-spacer>

						<!-- Actions -->
						<v-col cols="12" sm="auto" class="d-flex justify-end">
							<v-btn
								color="success"
								depressed
								@click="navigateToNewQuote()"
								:disabled="!canCreateQuote"
								:block="$vuetify.breakpoint.xsOnly"
							>
								<v-icon left small>mdi-plus</v-icon>
								New Quote
							</v-btn>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>
		</v-col>

		<v-col cols="12" class="flex-auto pt-0 quotes-table-container">
			<v-data-table
				:headers="headers"
				:items="quotes"
				:loading="loading"
				:options.sync="tableOptions"
				:server-items-length="totalCount"
				:footer-props="footerProps"
				:mobile-breakpoint="0"
				class="quotes-data-table"
				@click:row="navigateToQuote"
				must-sort
				fixed-header
			>
				<!-- Organization Column -->
				<template v-slot:item.organization="{ item }">
					<div class="d-flex align-center">
						<truncate-tooltip>{{ item.quoteCustomer.customer.organization }}</truncate-tooltip>
						<v-btn
							v-if="canEditCustomer"
							icon
							x-small
							class="ml-1 edit-customer-btn"
							@click.stop="navigateToCustomer(item.quoteCustomer.customerId)"
							title="Edit Customer"
						>
							<v-icon x-small>mdi-open-in-new</v-icon>
						</v-btn>
					</div>
				</template>

				<!-- Quote # Column (with Order # tooltip for converted quotes) -->
				<template v-slot:item.quoteNumber="{ item }">
					<v-tooltip bottom v-if="autoShowTooltipIds[item.quoteId]" :value="true">
						<template v-slot:activator="{ on, attrs }">
							<span v-bind="attrs" v-on="on">{{ item.quoteNumber }}</span>
						</template>
						<span>This quote is now Order #{{ item.order.orderNumber }}</span>
					</v-tooltip>
					<v-tooltip bottom v-else-if="item.order && item.order.orderNumber">
						<template v-slot:activator="{ on, attrs }">
							<span v-bind="attrs" v-on="on">{{ item.quoteNumber }}</span>
						</template>
						<span>This quote is now Order #{{ item.order.orderNumber }}</span>
					</v-tooltip>
					<span v-else>{{ item.quoteNumber }}</span>
				</template>

				<!-- Created Date Column -->
				<template v-slot:item.createdAt="{ item }">
					{{ formatDate(item.createdAt) }}
				</template>

				<!-- Expected Order Date Column (#1465) -->
				<template v-slot:item.expectedOrderDate="{ item }">
					<span v-if="item.expectedOrderDate">{{ formatDate(item.expectedOrderDate) }}</span>
				</template>

				<!-- Sales Rep Column -->
				<template v-slot:item.salesRepId="{ item }">
					{{ repSalesRepInitials(item.salesRepId) }}
				</template>

				<!-- Status Column -->
				<template v-slot:item.status="{ item }">
					<div class="table-status" :class="renderStatusColor(item.status)">
						{{ formatStatus(item.status) }}
					</div>
				</template>

				<!-- Total Column -->
				<template v-slot:item.total="{ item }">
					{{ renderPrice(item.computedTotal ?? item.total) }}
				</template>

				<!-- Balance Column -->
				<template v-slot:item.balance="{ item }">
					{{ renderPrice(item.computedBalance ?? item.balance) }}
				</template>

				<!-- Contact Column. The Email column was dropped to make room for Expected Order
				     Date (#1465); the address stays searchable and shows here on hover. -->
				<template v-slot:item.primaryContact="{ item }">
					<v-tooltip bottom v-if="item.quoteCustomer.contact && item.quoteCustomer.contact.email">
						<template v-slot:activator="{ on, attrs }">
							<div v-bind="attrs" v-on="on" class="text-truncate">
								{{ item.quoteCustomer.contact.firstName }} {{ item.quoteCustomer.contact.lastName }}
							</div>
						</template>
						<span>{{ item.quoteCustomer.contact.email }}</span>
					</v-tooltip>
					<truncate-tooltip v-else-if="item.quoteCustomer.contact">
						{{ item.quoteCustomer.contact.firstName }} {{ item.quoteCustomer.contact.lastName }}
					</truncate-tooltip>
				</template>

				<!-- Ref # Column -->
				<template v-slot:item.referenceNumber="{ item }">
					<truncate-tooltip v-if="item.referenceNumber">
						{{ item.referenceNumber }}
					</truncate-tooltip>
				</template>

				<!-- Loading slot -->
				<template v-slot:loading>
					<v-skeleton-loader type="table-row@10"></v-skeleton-loader>
				</template>

				<!-- No data slot -->
				<template v-slot:no-data>
					<div class="text-center pa-4">
						<v-icon large color="grey lighten-1">mdi-file-document-outline</v-icon>
						<p class="mt-2 grey--text">No quotes found</p>
					</div>
				</template>
			</v-data-table>
		</v-col>

		<!-- Cross-reference navigation dialog -->
		<v-dialog v-model="crossRefDialog.show" max-width="420">
			<v-card v-if="crossRefDialog.item">
				<v-card-title class="text-h6">Order Found</v-card-title>
				<v-card-text style="font-size: 1.4rem;">
					This quote is now Order #{{ crossRefDialog.item.order.orderNumber }}.
					<template v-if="isAdminUser">Would you like to view the quote or the order?</template>
					<template v-else>The quote is the snapshot as approved; the order is the live record.</template>
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn v-if="isAdminUser" text @click="goToQuoteFromDialog">View Quote</v-btn>
					<v-btn color="primary" depressed data-testid="cross-ref-go-to-order" @click="goToOrderFromDialog">
						{{ isAdminUser ? 'View Order' : 'Take me to the order' }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-row>
</template>

<script>
import { renderPrice } from "@/helpers/PricingCalculator";
import { SystemPermissions, WellKnownRoles } from "@/models/User";
import queries from "../../gql/queries";
import TruncateTooltip from "@/components/TruncateTooltip.vue";
import { migrateQuoteFilters, QUOTE_FILTERS_VERSION } from "@/utils/quoteListFilters";

export default {
	name: "QuotesList",
	components: { TruncateTooltip },
	data() {
		return {
			canCreateQuote: false,
			canEditCustomer: false,
			canFilterByRep: false,
			isAdminUser: false,
			autoShowTooltipIds: {},
			autoShowTooltipTimer: null,
			crossRefDialog: { show: false, item: null },
			loading: true,
			searchForm: {
				keywords: "",
			},
			quotes: [],
			salesReps: [],
			totalCount: 0,
			filterOptions: [
				{ text: "Incomplete", value: "INCOMPLETE" },
				{ text: "Final Check", value: "FINAL_CHECK" },
				{ text: "Ordered", value: "ORDERED" },
				{ text: "Cancelled", value: "CANCELLED" },
			],
			selectedFilters: ["INCOMPLETE", "FINAL_CHECK"],
			selectedSalesRepIds: [],
			searchTimeout: null,
			tableOptions: {
				page: 1,
				itemsPerPage: 15,
				sortBy: ["modifiedAt"],
				sortDesc: [true],
			},
			footerProps: {
				"items-per-page-options": [10, 15, 25, 50],
				"show-first-last-page": true,
			},
			headers: [
				{
					text: "School/Organization",
					value: "organization",
					sortable: true,
					width: "14%",
				},
				{
					text: "Quote #",
					value: "quoteNumber",
					sortable: true,
					width: "6%",
				},
				{
					text: "Order Date",
					value: "createdAt",
					sortable: true,
					width: "8%",
				},
				{
					text: "Expected Order Date",
					value: "expectedOrderDate",
					sortable: true,
					width: "9%",
				},
				{
					text: "Rep",
					value: "salesRepId",
					sortable: true,
					width: "4%",
				},
				{
					text: "Status",
					value: "status",
					sortable: true,
					width: "8%",
					align: "center",
				},
				{
					text: "Contact",
					value: "primaryContact",
					sortable: true,
					width: "12%",
				},
				{
					text: "Ref #",
					value: "referenceNumber",
					sortable: true,
					width: "15%",
				},
				{
					text: "Order Total",
					value: "total",
					align: "end", // money reads right-aligned (#357)
					sortable: true,
					width: "7%",
				},
				{
					text: "Balance",
					value: "balance",
					align: "end", // money reads right-aligned (#357)
					sortable: true,
					width: "7%",
				},
			],
		};
	},
	apollo: {
		quotesQuery: {
			query: () => queries.SEARCH_QUOTES,
			variables() {
				return this.buildQueryVariables();
			},
			update(data) {
				this.loading = false;
				this.totalCount = data.quotes.totalCount;
				this.quotes = data.quotes.items;
				this.updateAutoShowTooltips();
				return data.quotes;
			},
			error(error) {
				console.error("Error fetching quotes:", error);
				this.loading = false;
			},
		},
		salesReps: {
			query: queries.LIST_SALES_REPS,
			variables: {
				take: 500,
				offset: 0,
			},
			update: (data) => data.salesReps.items,
		},
	},
	computed: {
		navigationDirection() {
			return this.$store.getters["globalState/navigationDirection"];
		},
		salesRepOptions() {
			if (!this.salesReps) return [];
			return [
				...this.salesReps.filter((rep) => !rep.inactiveDate),
			];
		},
	},
	watch: {
		tableOptions: {
			handler() {
				this.fetchData();
			},
			deep: true,
		},
		navigationDirection: {
			handler(newDirection) {
				if (newDirection) {
					const savedState = this.$store.getters["globalState/quotesListState"];
					if (savedState) {
						this.tableOptions.page = savedState.page + 1; // Convert 0-indexed to 1-indexed
						this.selectedFilters = migrateQuoteFilters(savedState.filters, savedState.filtersVersion);
						this.searchForm.keywords = savedState.searchKeywords;
						if (savedState.salesRepIds && savedState.salesRepIds.length > 0) {
							this.selectedSalesRepIds = [...savedState.salesRepIds];
						}
					}
				}
			},
			immediate: true,
		},
	},
	methods: {
		formatDate(date) {
			if (!date) return "";
			date = date.substring(0, 10);
			const [year, month, day] = date.split("-");
			return `${month}/${day}/${year}`;
		},
		formatStatus(status) {
			if (!status) return "";
			const statusMap = {
				PENDING_APPROVAL: "Pending Approval",
				INCOMPLETE: "Incomplete",
				FINAL_CHECK: "Final Check",
				ORDERED: "Ordered",
				CANCELED: "Canceled",
				CANCELLED: "Cancelled",
				PENDING: "Pending",
				INPROCESS: "In Process",
				SHIPPED: "Shipped",
			};
			return statusMap[status] || status;
		},
		renderPrice,
		updateAutoShowTooltips() {
			if (this.autoShowTooltipTimer) clearTimeout(this.autoShowTooltipTimer);
			const keyword = (this.searchForm.keywords || "").trim().toLowerCase();
			if (!keyword) {
				this.autoShowTooltipIds = {};
				return;
			}
			const ids = {};
			for (const quote of this.quotes) {
				if (quote.order && quote.order.orderNumber) {
					const matchesQuoteNumber = quote.quoteNumber && quote.quoteNumber.toLowerCase().includes(keyword);
					const matchesOrderNumber = quote.order.orderNumber.toLowerCase().includes(keyword);
					if (matchesQuoteNumber || matchesOrderNumber) {
						ids[quote.quoteId] = true;
					}
				}
			}
			this.autoShowTooltipIds = ids;
			if (Object.keys(ids).length > 0) {
				this.autoShowTooltipTimer = setTimeout(() => {
					this.autoShowTooltipIds = {};
				}, 6000);
			}
		},
		repSalesRepInitials(salesRepId) {
			const repById = this.salesReps.find((obj) => obj.salesRepId == salesRepId);
			if (!repById) return "";
			const tokens = repById.name.split(/\s+/).filter((token) => token.length > 0);
			if (tokens.length === 1) {
				return tokens[0];
			}
			const initials = tokens.map((token) => token[0].toUpperCase());
			return initials.join("");
		},
		renderStatusColor(statusLabel) {
			const statusColors = {
				PENDING_APPROVAL: "table-status-blue",
				INCOMPLETE: "table-status-orange",
				FINAL_CHECK: "table-status-light-blue",
				CANCELED: "table-status-red",
				CANCELLED: "table-status-red",
				ORDERED: "table-status-green",
				PENDING: "table-status-orange",
				INPROCESS: "table-status-orange",
				SHIPPED: "table-status-green",
			};
			return statusColors[statusLabel] || "";
		},
		navigateToCustomer(customerId) {
			if (customerId) {
				this.$router.push(`/relationships/customer/${customerId}`);
			}
		},
		navigateToNewQuote() {
			this.$router.push({ path: "/sales-orders/quotes/new" });
		},
		navigateToQuote(quoteData) {
			// A quote that became an order always gets the prompt, whatever was searched for (#1473:
			// it used to fire only when the keyword matched a quote or order number, so a name search
			// dropped reps straight onto the approved snapshot). Reps get a single action to the order;
			// admins keep the quote view for checking paperwork.
			const hasOrder = quoteData.order && quoteData.order.salesOrderId;
			if (hasOrder) {
				this.crossRefDialog = { show: true, item: quoteData };
				return;
			}
			this.$router.push(`/sales-orders/quotes/${quoteData.quoteId}`);
		},
		goToOrderFromDialog() {
			const item = this.crossRefDialog.item;
			this.crossRefDialog = { show: false, item: null };
			this.$router.push(`/sales-orders/orders/${item.order.salesOrderId}`);
		},
		goToQuoteFromDialog() {
			const item = this.crossRefDialog.item;
			this.crossRefDialog = { show: false, item: null };
			this.$router.push(`/sales-orders/quotes/${item.quoteId}`);
		},
		onSearchInput() {
			// Debounced search
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.searchTimeout = setTimeout(() => {
				this.tableOptions.page = 1;
				this.fetchData();
			}, 400);
		},
		onSearchClear() {
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.tableOptions.page = 1;
			this.fetchData();
		},
		onFilterChange() {
			this.tableOptions.page = 1;
			this.fetchData();
		},
		onFilterClear() {
			this.$nextTick(() => {
				this.tableOptions.page = 1;
				this.fetchData();
			});
		},
		// Map table column values to GraphQL sort field names
		getSortFieldName(columnValue) {
			const sortFieldMap = {
				organization: "quoteCustomer.customer.organization",
				quoteNumber: "quoteNumber",
				createdAt: "createdAt",
				expectedOrderDate: "expectedOrderDate",
				salesRepId: "salesRepId",
				status: "status",
				referenceNumber: "referenceNumber",
				primaryContact: "quoteCustomer.contact.lastName",
				total: "total",
				balance: "balance",
				modifiedAt: "modifiedAt",
			};
			return sortFieldMap[columnValue] || "modifiedAt";
		},

		// Build nested sort object from dot notation (e.g., "quoteCustomer.customer.organization")
		buildSortObject(fieldPath, direction) {
			const parts = fieldPath.split(".");
			if (parts.length === 1) {
				return { [parts[0]]: direction };
			}
			// Build nested object from inside out
			let result = { [parts[parts.length - 1]]: direction };
			for (let i = parts.length - 2; i >= 0; i--) {
				result = { [parts[i]]: result };
			}
			return result;
		},

		buildQueryVariables() {
			const { page, itemsPerPage, sortBy, sortDesc } = this.tableOptions;
			const keyword = (this.searchForm.keywords || "").trim();

			// Build where clause
			const where = {
				and: [
					{
						or: [
							{ referenceNumber: { contains: keyword } },
							{ quoteNumber: { contains: keyword } },
							{ order: { orderNumber: { contains: keyword } } },
							{ quoteCustomer: { customer: { organization: { contains: keyword } } } },
							{ quoteCustomer: { customer: { customerNumber: { contains: keyword } } } },
							{ quoteCustomer: { contact: { firstName: { contains: keyword } } } },
							{ quoteCustomer: { contact: { lastName: { contains: keyword } } } },
							{ quoteCustomer: { contact: { email: { contains: keyword } } } },
						],
					},
					// Exclude PENDING_APPROVAL status (shown in separate tab)
					{
						status: { neq: "PENDING_APPROVAL" },
					},
				],
			};

			// Add status filter
			if (this.selectedFilters === null) {
				this.selectedFilters = [];
			}
			if (this.selectedFilters.length > 0) {
				// When searching, include ORDERED status so converted quotes are findable
				const filters = [...this.selectedFilters];
				if (keyword && !filters.includes("ORDERED")) {
					filters.push("ORDERED");
				}
				where.and.push({
					or: filters.map((filter) => ({ status: { eq: filter } })),
				});
			}

			// Add sales rep filter
			if (this.selectedSalesRepIds && this.selectedSalesRepIds.length > 0) {
				where.and.push({
					salesRepId: { in: this.selectedSalesRepIds },
				});
			}

			// Build dynamic sort order
			const sortColumn = sortBy[0] || "modifiedAt";
			const sortDirection = sortDesc[0] ? "DESC" : "ASC";
			const sortFieldPath = this.getSortFieldName(sortColumn);
			const order = [this.buildSortObject(sortFieldPath, sortDirection)];

			// Determine if we should include all reps' quotes
			// For sales reps: include all reps when they've selected a rep filter OR when searching with keywords
			// For admins: always include all reps
			const user = this.$store.getters.userInfo;
			const isAdmin = this.$userService.isAdmin(user);
			const isSalesRep = this.$userService.hasRole(user, WellKnownRoles.REP);
			const hasRepFilterSelected = Boolean(this.selectedSalesRepIds && this.selectedSalesRepIds.length > 0);
			const hasSearchKeyword = Boolean(keyword && keyword.length > 0);
			const includeAllReps = Boolean(isAdmin || (isSalesRep && (hasRepFilterSelected || hasSearchKeyword)));
			console.log('[QuotesList] includeAllReps debug:', { isAdmin, isSalesRep, hasRepFilterSelected, hasSearchKeyword, includeAllReps, selectedSalesRepIds: this.selectedSalesRepIds, userRoles: user?.roles });

			return {
				offset: (page - 1) * itemsPerPage,
				take: itemsPerPage,
				order,
				searchTerm: "",
				where,
				includeAllReps,
			};
		},
		async fetchData() {
			this.loading = true;
			if (this.$apollo.queries.quotesQuery) {
				await this.$apollo.queries.quotesQuery.refetch();
			}
		},
	},
	mounted() {
		const user = this.$store.getters.userInfo;
		this.canCreateQuote =
			this.$userService.isAdmin(user) ||
			this.$userService.hasPermission(user, SystemPermissions.SalesOrders_Quote_Create);
		this.canEditCustomer =
			this.$userService.isAdmin(user) ||
			this.$userService.hasPermission(user, SystemPermissions.Relationships_Customer_ALL);
		// Only admin users can filter by sales rep (reps should only see their own quotes)
		this.canFilterByRep = this.$userService.isAdmin(user);
		this.isAdminUser = this.canFilterByRep;
	},
	beforeDestroy() {
		if (this.searchTimeout) clearTimeout(this.searchTimeout);
		if (this.autoShowTooltipTimer) clearTimeout(this.autoShowTooltipTimer);
	},
	beforeRouteLeave(to, from, next) {
		this.$store.dispatch("globalState/saveQuotesListState", {
			page: this.tableOptions.page - 1, // Convert back to 0-indexed
			filters: [...this.selectedFilters],
			filtersVersion: QUOTE_FILTERS_VERSION,
			searchKeywords: this.searchForm.keywords,
			salesRepIds: [...this.selectedSalesRepIds],
			scrollPosition: window.scrollY,
		});
		next();
	},
};
</script>

<style lang="scss" scoped>
.quotes-page {
	// Use calc to fit within viewport: 100vh - header (64px) - container padding (24px)
	height: calc(100vh - 88px);
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.quotes-table-container {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0; // Critical for flex children to respect overflow
	overflow: hidden;
}

.quotes-toolbar-card {
	border-radius: 4px 4px 0 0 !important;
	border-bottom: 1px solid $color-gray;
	flex-shrink: 0; // Don't shrink the toolbar
}

.quotes-search {
	::v-deep .v-input__slot {
		background-color: $color-gray-light-2 !important;
	}
}

.quotes-data-table {
	border-radius: 0 0 4px 4px !important;
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0; // Critical for flex children to respect overflow
	overflow: hidden;

	// Table wrapper should scroll
	::v-deep .v-data-table__wrapper {
		flex: 1;
		overflow-y: auto;

		table {
			table-layout: fixed;
			width: 100%;
		}
	}

	// Keep footer fixed at bottom
	::v-deep .v-data-footer {
		flex-shrink: 0;
	}
	// Make rows clickable
	::v-deep tbody tr {
		cursor: pointer;
		transition: background-color 0.2s;

		&:hover {
			background-color: $color-gray-light !important;
		}
	}

	// Match existing header styling
	::v-deep > .v-data-table__wrapper > table > thead > tr > th {
		font-size: 1.4rem !important;
		font-weight: 500 !important;
		color: #000 !important;
		text-transform: uppercase;
		background-color: $color-gray-light-2 !important;
		white-space: nowrap;
	}

	// Match existing row height and font size
	::v-deep > .v-data-table__wrapper > table > tbody > tr > td {
		height: 60px !important;
		font-size: 1.6rem !important;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	// Footer styling
	::v-deep .v-data-footer {
		font-size: 1.25rem !important;
	}

	::v-deep .v-data-footer__select .v-select__selections .v-select__selection--comma {
		font-size: 1.15rem !important;
	}

	// Edit customer button - visible on row hover
	.edit-customer-btn {
		opacity: 0;
		transition: opacity 0.2s;
	}

	::v-deep tbody tr:hover .edit-customer-btn {
		opacity: 1;
	}

	// Status badge styling (reuse existing)
	.table-status {
		display: inline-block;
		border: 1px solid;
		border-radius: 3px;
		font-size: 1.4rem;
		padding: 0.5rem;
		min-width: 9rem;
		text-align: center;

		&-orange {
			border-color: $color-orange;
			color: $color-orange;
		}

		&-red {
			border-color: red;
			color: red;
		}

		&-green {
			border-color: $color-green;
			color: $color-green;
		}

		&-blue {
			border-color: $color-blue;
			color: $color-blue;
		}

		&-light-blue {
			border-color: #29b6f6;
			color: #0288d1;
		}

		&-gray {
			border-color: $color-gray-dark;
			color: $color-gray-dark;
		}
	}
}
</style>
